import { ChangeDetectionStrategy, Component, Inject, OnDestroy, Optional } from "@angular/core";
import { EneLayoutService } from "src/app/core/services/layout.service";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, merge, Observable } from "rxjs";
import { map, scan } from "rxjs/operators";
import { IButtonToggleItem } from "src/app/common/components/button-toggle/button-toggle.component";
import { SLOT } from "src/app/common/enums/slot";
import { ContentfulActionField, ContentfulContentType } from "src/app/core/interfaces/contentful";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { OVERLAY_DATA, OverlayService } from "src/app/core/services/overlay.service";
import { fetchPublicContentfulEntries } from "src/app/ngrx/actions/contentful.actions";
import { updateUsersSettingsUsersStorage } from "src/app/ngrx/actions/users-settings.actions";
import { RootState } from "src/app/ngrx/root-reducers";
import { NewsActionsManagerService } from "../news-actions-manager.service";
import { CommunalPlatformNewsEntry, NetzeBwNewsEntry, NewsType } from "../news.interfaces";
import { NewsService } from "../news.service";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "tpm-news",
  templateUrl: "./news.component.html",
  styleUrls: ["./news.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsComponent implements OnDestroy {
  public readonly contentTypeToggles: Array<IButtonToggleItem>;
  public readonly contentType$ = new BehaviorSubject<ContentfulContentType>(undefined);
  public readonly search$ = new BehaviorSubject<string>("");
  public readonly module$ = new BehaviorSubject<Array<SLOT>>([]);

  public readonly selectedNews$ = this.newsService.selectedNews$;

  public readonly vm$ = merge(
    this.layoutService.isMobileScreen$.pipe(map((isMobileView) => ({ isMobileView }))),
    this.contentType$.pipe(map((contentType) => ({ ...this.getNewsType(contentType), contentType }))),
    this.getNews()
  ).pipe(scan((vm, values) => ({ ...vm, ...values }), {}));
  public readonly hideModuleSelectOptions$ = this.contentType$
    .asObservable()
    .pipe(
      map(
        (contentType) =>
          contentType === ContentfulContentType.NBRegulatedModulesNews ||
          contentType === ContentfulContentType.NBServicesModulesNews
      )
    );

  public readonly contentType: ContentfulContentType;

  constructor(
    @Optional()
    @Inject(OVERLAY_DATA)
    private readonly overlayData: { contentTypes: Array<ContentfulContentType>; contentType: ContentfulContentType },
    private readonly newsService: NewsService,
    private readonly layoutService: EneLayoutService,
    private readonly overlayService: OverlayService,
    private readonly newsActionsManagerService: NewsActionsManagerService,
    private readonly store: Store<RootState>,
    private readonly analytics: AnalyticsService
  ) {
    if (this.overlayData?.contentTypes) {
      this.contentTypeToggles = this.overlayData.contentTypes.map((value) => ({
        label: `CONTENTFUL.NEWS.TOGGLES.${value}`,
        value
      }));

      this.contentType = this.overlayData?.contentType || (this.contentTypeToggles[0].value as ContentfulContentType);
    }
  }

  public readonly trackById = (_: number, entry: CommunalPlatformNewsEntry | NetzeBwNewsEntry): string => entry.id;

  public get headline(): string {
    switch (this.contentType) {
      case ContentfulContentType.NBRegulatedModulesNews:
      case ContentfulContentType.CPRegulatedModulesNews:
      case ContentfulContentType.RegulatedModulesWebinars: {
        return "CONTENTFUL.NEWS.REGULATED_TITLE";
      }
      case ContentfulContentType.NBServicesModulesNews:
      case ContentfulContentType.ServicesModulesWebinars:
      case ContentfulContentType.CPServicesModulesNews: {
        return "CONTENTFUL.NEWS.MARKETPLACE_TITLE";
      }
      default:
        return "CONTENTFUL.NEWS.TITLE";
    }
  }

  public onSearchChange(search: string): void {
    this.search$.next(search);
  }

  public onModuleChange(modules: Array<SLOT>): void {
    this.module$.next(modules);
  }

  public onContentTypeChange(contentType: ContentfulContentType): void {
    if (this.contentType$.value) {
      this.analytics.trackNewsTypeChange(contentType);
    }

    this.contentType$.next(contentType);
    this.store.dispatch(fetchPublicContentfulEntries({ contentType }));
  }

  public onCardNewsClick(
    contentType: ContentfulContentType,
    selected: CommunalPlatformNewsEntry | NetzeBwNewsEntry
  ): void {
    if (selected.isNew) {
      this.store.dispatch(updateUsersSettingsUsersStorage({ items: [{ key: selected.id, value: true }] }));
    }
    this.newsService.setSelectedNews(selected);
    this.analytics.trackNewsItemClick(contentType, selected?.headline);
  }

  public onListItemNewsClick(
    action: ContentfulActionField | void,
    selected: CommunalPlatformNewsEntry,
    contentType: ContentfulContentType
  ): void {
    if (selected.isNew) {
      this.store.dispatch(updateUsersSettingsUsersStorage({ items: [{ key: selected.id, value: true }] }));
    }

    if (action) {
      this.newsActionsManagerService.dispatch(action.action, action.url, selected);
    }

    this.analytics.trackNewsItemClick(contentType, selected?.headline);
  }

  public onNewsDetailsClose(): void {
    this.newsService.setSelectedNews(null);
  }

  private getNews(): Observable<{ entries: Array<CommunalPlatformNewsEntry | NetzeBwNewsEntry> }> {
    return combineLatest([this.newsService.getNewsEntries(), this.search$, this.module$]).pipe(
      map(([_entries, search, modules]) => ({
        entries: _entries
          // filter by search
          .filter(({ headline }) => headline?.toLowerCase().includes(search))
          // filter by module
          .filter((entry) => {
            if (!("moduleName" in entry)) {
              return true;
            }

            if (!modules || modules.length === 0) {
              return true;
            }

            return modules.includes(entry.moduleName as SLOT);
          })
      }))
    );
  }

  private getNewsType(contentType: ContentfulContentType): { newsType: NewsType } {
    switch (contentType) {
      case ContentfulContentType.CPRegulatedModulesNews:
      case ContentfulContentType.CPServicesModulesNews: {
        return { newsType: "list-item" };
      }
      case ContentfulContentType.NBRegulatedModulesNews:
      case ContentfulContentType.NBServicesModulesNews: {
        return { newsType: "card" };
      }
      default: {
        return { newsType: "list-item" };
      }
    }
  }

  public ngOnDestroy(): void {
    this.newsService.setSelectedNews(null);
  }
}
